import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useToast } from './Toast';

const PRESETS_SECTEURS = {
  informatique: {
    label: '💻 Informatique & Consulting IT',
    mentions: 'Pénalités de retard : 3 fois le taux d\'intérêt légal. Indemnité forfaitaire pour frais de recouvrement : 40 €.',
    lignes: [
      { description: 'Prestation de développement web / conseil', quantite: 1, prix_unitaire_ht: 650.0, taux_tva: 20.0 },
      { description: 'Maintenance applicative mensuelle', quantite: 1, prix_unitaire_ht: 150.0, taux_tva: 20.0 }
    ]
  },
  btp: {
    label: '🏗️ BTP, Artisans & Travaux',
    mentions: 'Autoliquidation de la TVA par le preneur (Art. 283 du CGI) si sous-traitance. Assurance Décennale obligatoire.',
    lignes: [
      { description: 'Main d\'œuvre spécialisée (heures)', quantite: 8, prix_unitaire_ht: 45.0, taux_tva: 10.0 },
      { description: 'Fourniture et livraison de matériaux', quantite: 1, prix_unitaire_ht: 320.0, taux_tva: 20.0 }
    ]
  },
  commerce: {
    label: '🛍️ Commerce & Vente de Marchandises',
    mentions: 'Réserve de propriété : Les marchandises restent notre propriété jusqu au paiement complet du prix.',
    lignes: [
      { description: 'Vente d\'articles / produits réf. 001', quantite: 5, prix_unitaire_ht: 29.90, taux_tva: 20.0 },
      { description: 'Frais de port et conditionnement', quantite: 1, prix_unitaire_ht: 8.50, taux_tva: 20.0 }
    ]
  },
  formation: {
    label: '🎓 Formation & Services à la personne',
    mentions: 'Organisme de formation enregistré sous le numéro de déclaration d activité auprès du Préfet de région.',
    lignes: [
      { description: 'Session de formation professionnelle (journée)', quantite: 1, prix_unitaire_ht: 850.0, taux_tva: 20.0 }
    ]
  },
  micro: {
    label: '👤 Micro-Entrepreneur / Freelance',
    mentions: 'TVA non applicable, article 293 B du Code Général des Impôts (CGI).',
    lignes: [
      { description: 'Prestation de service / mission', quantite: 1, prix_unitaire_ht: 400.0, taux_tva: 0.0 }
    ]
  }
};

const FacturationEinvoicing = ({ clientId }) => {
  const { addToast } = useToast();
  const [factures, setFactures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 768 : false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [activeTab, setActiveTab] = useState('toutes');
  const [form, setForm] = useState({
    type_document: 'facture',
    client_nom: '',
    client_email: '',
    client_siren: '',
    client_adresse: '',
    date_facture: new Date().toISOString().split('T')[0],
    secteur_activite: 'informatique',
    conditions_reglement: '30_jours',
    mentions_legales: PRESETS_SECTEURS.informatique.mentions,
    iban: '',
    bic: '',
    envoyer_email: true,
    lignes: PRESETS_SECTEURS.informatique.lignes
  });
  const [clientsRecurrents, setClientsRecurrents] = useState([]);

  useEffect(() => {
    loadFactures();
  }, [clientId]);

  useEffect(() => {
    if (showCreateModal) {
      loadClientsRecurrents();
    }
  }, [showCreateModal, clientId]);

  const loadClientsRecurrents = async () => {
    try {
      const resp = await api.get(`/factures/${clientId}/clients-recurrents`);
      if (resp.success) {
        setClientsRecurrents(resp.clients || []);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleSelectClientRecurrent = (clientNom) => {
    const selected = clientsRecurrents.find(c => c.client_nom === clientNom);
    if (selected) {
      setForm({
        ...form,
        client_nom: selected.client_nom,
        client_email: selected.client_email || form.client_email,
        client_siren: selected.client_siren || form.client_siren,
        client_adresse: selected.client_adresse || form.client_adresse,
        secteur_activite: selected.secteur_activite || form.secteur_activite,
        conditions_reglement: selected.conditions_reglement || form.conditions_reglement,
        mentions_legales: selected.mentions_legales || form.mentions_legales,
        iban: selected.iban || form.iban,
        bic: selected.bic || form.bic
      });
      addToast(`👥 Coordonnées de "${selected.client_nom}" pré-remplies !`, 'success');
    }
  };

  const loadFactures = async () => {
    setLoading(true);
    try {
      const resp = await api.get(`/factures/${clientId}`);
      if (resp && (resp.success || resp.factures)) {
        setFactures(resp.factures || []);
      } else {
        setFactures([]);
      }
    } catch (err) {
      console.error(err);
      setFactures([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSecteurChange = (secteurKey) => {
    const preset = PRESETS_SECTEURS[secteurKey];
    if (preset) {
      setForm({
        ...form,
        secteur_activite: secteurKey,
        mentions_legales: preset.mentions,
        lignes: preset.lignes.map(l => ({ ...l }))
      });
      addToast(`📋 Trame appliquée : ${preset.label}`, 'info');
    }
  };

  const handleAddLigne = () => {
    setForm({
      ...form,
      lignes: [...form.lignes, { description: '', quantite: 1, prix_unitaire_ht: 0, taux_tva: 20.0 }]
    });
  };

  const handleRemoveLigne = (idx) => {
    const newLignes = [...form.lignes];
    newLignes.splice(idx, 1);
    setForm({ ...form, lignes: newLignes });
  };

  const handleLigneChange = (idx, field, value) => {
    const newLignes = [...form.lignes];
    newLignes[idx][field] = value;
    setForm({ ...form, lignes: newLignes });
  };

  const totalHT = form.lignes.reduce((sum, lg) => sum + (parseFloat(lg.quantite || 0) * parseFloat(lg.prix_unitaire_ht || 0)), 0);
  const totalTVA = form.lignes.reduce((sum, lg) => sum + ((parseFloat(lg.quantite || 0) * parseFloat(lg.prix_unitaire_ht || 0)) * (parseFloat(lg.taux_tva || 0) / 100)), 0);
  const totalTTC = totalHT + totalTVA;

  const handleCreateInvoice = async (e) => {
    e.preventDefault();
    if (!form.client_nom) {
      addToast('⚠️ Veuillez renseigner le nom du client', 'error');
      return;
    }

    setCreating(true);
    try {
      const resp = await api.post(`/factures/${clientId}/creer`, form);
      if (resp.success) {
        const docLabel = form.type_document === 'devis' ? 'Devis' : 'Facture';
        addToast(`✅ ${docLabel} ${resp.numero} créé(e) avec succès !`, 'success');
        if (form.envoyer_email && form.client_email) {
          addToast(`📧 Envoi de l'e-mail en arrière-plan vers ${form.client_email}...`, 'info');
        }
        setShowCreateModal(false);
        loadFactures();
      } else {
        addToast('❌ Erreur lors de la création', 'error');
      }
    } catch (err) {
      console.error(err);
      addToast('❌ Erreur de connexion', 'error');
    } finally {
      setCreating(false);
    }
  };

  const handleConvertirDevis = async (devisId, numero) => {
    try {
      addToast(`⚡ Conversion du devis ${numero} en Facture...`, 'info');
      const resp = await api.post(`/factures/${clientId}/${devisId}/convertir`);
      if (resp.success) {
        addToast(`✅ Devis ${numero} converti avec succès en Facture ${resp.numero} !`, 'success');
        loadFactures();
      } else {
        addToast('❌ Erreur lors de la conversion', 'error');
      }
    } catch (err) {
      console.error(err);
      addToast('❌ Erreur de connexion', 'error');
    }
  };

  const handleTeletransmettrePPF = async (factureId) => {
    try {
      addToast('🚀 Télétransmission au Portail Public (PPF / Chorus Pro)...', 'info');
      const resp = await api.post(`/factures/${clientId}/${factureId}/teletransmettre`);
      if (resp.success) {
        addToast(`🏛️ Facture télétransmise au PPF ! Récépissé N° ${resp.numero_depot_ppf}`, 'success');
        loadFactures();
      } else {
        addToast('❌ Erreur lors de la télétransmission', 'error');
      }
    } catch (err) {
      console.error(err);
      addToast('❌ Erreur de télétransmission API', 'error');
    }
  };

  const handleUpdateStatutVie = async (factureId, newStatut) => {
    try {
      const resp = await api.post(`/factures/${clientId}/${factureId}/statut-vie`, {
        statut_vie: newStatut,
        date_encaissement: new Date().toISOString().split('T')[0]
      });
      if (resp.success) {
        addToast(`✅ Statut mis à jour : ${newStatut}`, 'success');
        loadFactures();
      } else {
        addToast('❌ Erreur de mise à jour', 'error');
      }
    } catch (err) {
      console.error(err);
      addToast('❌ Erreur de connexion', 'error');
    }
  };

  const handleDownloadEReportingXML = async () => {
    try {
      addToast('⏳ Génération du fichier XML e-Reporting DGFiP...', 'info');
      const targetId = clientId || '845364371';
      const blob = await api.request(`/factures/${targetId}/ereporting/xml`, {
        responseType: 'blob'
      });

      if (blob && (blob instanceof Blob) && blob.size > 0) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `eReporting_DGFiP_${new Date().toISOString().split('T')[0]}.xml`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        addToast('✅ XML e-Reporting téléchargé !', 'success');
      } else {
        addToast('❌ Erreur de génération du fichier e-Reporting', 'error');
      }
    } catch (err) {
      console.error(err);
      addToast('❌ Erreur de téléchargement e-Reporting', 'error');
    }
  };

  const handleDownloadFacturX = async (factureId, numero) => {
    try {
      addToast('⏳ Génération du Factur-X certifié...', 'info');
      const targetId = clientId || '845364371';
      const blob = await api.request(`/factures/${targetId}/${factureId}/download`, {
        responseType: 'blob'
      });

      if (blob && (blob instanceof Blob) && blob.size > 0) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `FacturX_${numero}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        addToast('✅ Factur-X téléchargé !', 'success');
      } else {
        addToast('❌ Erreur de génération du Factur-X', 'error');
      }
    } catch (err) {
      console.error(err);
      addToast('❌ Erreur de téléchargement Factur-X', 'error');
    }
  };

  const formatEuro = (val) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(val || 0);
  };

  const handleLogoUpload = async (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const formData = new FormData();
      formData.append('file', file);

      try {
        addToast('⏳ Enregistrement du logo...', 'info');
        const targetId = clientId || '845364371';
        const res = await api.upload(`/factures/${targetId}/logo`, formData);
        if (res && res.success) {
          addToast('✅ Logo d\'entreprise enregistré pour vos factures !', 'success');
        } else {
          addToast('❌ Erreur lors de l\'enregistrement du logo', 'error');
        }
      } catch (err) {
        console.error(err);
        addToast('❌ Impossible d\'enregistrer le logo', 'error');
      }
    }
  };

  return (
    <div style={{ fontFamily: 'Inter, system-ui, sans-serif', background: '#F8FAFC', padding: '24px', minHeight: '100vh', boxSizing: 'border-box' }}>
      
      {/* Header */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px', gap: '16px' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <h1 style={{ fontSize: '24px', fontWeight: '800', color: '#0F172A', letterSpacing: '-0.5px', margin: 0 }}>
              🧾 Facturation, Factur-X & Télétransmission PPF
            </h1>
            <span style={{ background: '#E1F5EE', color: '#0F6E56', border: '1px solid #A7F3D0', fontSize: '11px', fontWeight: '700', padding: '4px 10px', borderRadius: '12px' }}>
              ✓ Conforme Réforme 2026/2027 (DGFiP)
            </span>
          </div>
          <p style={{ fontSize: '14px', color: '#64748B', marginTop: '4px', margin: '4px 0 0' }}>
            Télétransmission API directe vers le Portail Public (PPF / Chorus Pro), scellement SHA-256 et e-Reporting.
          </p>
        </div>

        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            type="file"
            accept="image/png,image/jpeg,image/jpg,image/webp"
            id="logo-upload-input"
            onChange={handleLogoUpload}
            style={{ display: 'none' }}
          />
          <label
            htmlFor="logo-upload-input"
            style={{
              padding: '10px 14px', background: '#FFFFFF', color: '#334155',
              border: '1px solid #CBD5E1', borderRadius: '10px', fontSize: '13px',
              fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px',
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
            }}
            title="Importer le logo de votre entreprise (PNG, JPG) pour l'intégrer sur vos factures PDF"
          >
            <span>📷</span> Logo Entreprise
          </label>

          <button
            onClick={handleDownloadEReportingXML}
            style={{
              padding: '10px 16px', background: '#F1F5F9', color: '#0F172A',
              border: '1px solid #CBD5E1', borderRadius: '10px', fontSize: '13px',
              fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px'
            }}
          >
            <span>📊</span> Exporter e-Reporting DGFiP (XML)
          </button>

          <button
            onClick={() => { setForm(prev => ({ ...prev, type_document: 'devis' })); setShowCreateModal(true); }}
            style={{
              padding: '10px 16px', background: '#F3E8FF', color: '#7E22CE',
              border: '1px solid #D8B4FE', borderRadius: '10px', fontSize: '13.5px',
              fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px'
            }}
          >
            <span>📝</span> Créer un Devis
          </button>

          <button
            onClick={() => { setForm(prev => ({ ...prev, type_document: 'facture' })); setShowCreateModal(true); }}
            style={{
              padding: '10px 18px', background: 'linear-gradient(135deg, #1D9E75, #10B981)',
              color: 'white', border: 'none', borderRadius: '10px', fontSize: '13.5px',
              fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px',
              boxShadow: '0 4px 12px rgba(29, 158, 117, 0.15)'
            }}
          >
            <span>➕</span> Créer une Facture Factur-X
          </button>
        </div>
      </div>

      {/* Onglets de filtrage */}
      {(() => {
        const isDevisDoc = (f) => f.type_document === 'devis' || f.numero?.startsWith('DEV-');
        return (
          <>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
              {[
                { key: 'toutes', label: `📄 Tous les documents (${factures.length})` },
                { key: 'factures', label: `🧾 Factures (${factures.filter(f => !isDevisDoc(f)).length})` },
                { key: 'devis', label: `📝 Devis (${factures.filter(f => isDevisDoc(f)).length})` }
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '8px',
                    fontSize: '13px',
                    fontWeight: activeTab === tab.key ? '700' : '500',
                    border: activeTab === tab.key ? '1px solid #1D9E75' : '1px solid #E2E8F0',
                    background: activeTab === tab.key ? '#E1F5EE' : 'white',
                    color: activeTab === tab.key ? '#0F6E56' : '#64748B',
                    cursor: 'pointer'
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Main List Table */}
            {loading ? (
              <div style={{ textAlign: 'center', padding: '60px 0', background: 'white', borderRadius: '16px', border: '1px solid #E2E8F0' }}>
                <div className="animate-spin" style={{ width: '36px', height: '36px', border: '3px solid #1D9E75', borderTopColor: 'transparent', borderRadius: '50%', margin: '0 auto 12px' }}></div>
                <p style={{ color: '#64748B', fontSize: '14px' }}>Chargement de vos factures et registres...</p>
              </div>
            ) : factures.filter(f => {
                if (activeTab === 'factures') return !isDevisDoc(f);
                if (activeTab === 'devis') return isDevisDoc(f);
                return true;
              }).length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 20px', background: 'white', borderRadius: '16px', border: '1px solid #E2E8F0' }}>
                <span style={{ fontSize: '40px' }}>📄</span>
                <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#0F172A', marginTop: '12px' }}>Aucun document dans cette catégorie</h3>
                <p style={{ fontSize: '13.5px', color: '#64748B', maxWidth: '420px', margin: '6px auto 16px' }}>
                  Créez votre premier devis ou votre première facture certifiée.
                </p>
              </div>
            ) : (
              <div style={{ background: 'white', borderRadius: '16px', border: '1px solid #E2E8F0', overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13.5px' }}>
                  <thead>
                    <tr style={{ background: '#F8FAFC', borderBottom: '1px solid #E2E8F0', color: '#64748B', fontSize: '12px', textTransform: 'uppercase' }}>
                      <th style={{ padding: '14px 20px' }}>Type & Numéro</th>
                      <th style={{ padding: '14px 20px' }}>Client</th>
                      <th style={{ padding: '14px 20px' }}>Total TTC</th>
                      <th style={{ padding: '14px 20px' }}>Télétransmission PPF</th>
                      <th style={{ padding: '14px 20px' }}>Statut / Cycle</th>
                      <th style={{ padding: '14px 20px', textAlign: 'right' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {factures.filter(f => {
                      if (activeTab === 'factures') return !isDevisDoc(f);
                      if (activeTab === 'devis') return isDevisDoc(f);
                      return true;
                    }).map((fac) => {
                      const isDevis = isDevisDoc(fac);
                      const isConverted = isDevis && fac.statut_vie === 'converti';
                      const isPaid = fac.statut_vie === 'encaissee';
                      const isTransmitted = fac.statut_ppf === 'transmis';

                return (
                  <tr key={fac.id} style={{ borderBottom: '1px solid #F1F5F9' }}>
                    <td style={{ padding: '14px 20px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{
                          fontSize: '10.5px', fontWeight: '800', padding: '2px 8px', borderRadius: '6px',
                          background: isDevis ? '#F3E8FF' : '#E0F2FE',
                          color: isDevis ? '#7E22CE' : '#0369A1'
                        }}>
                          {isDevis ? '📝 DEVIS' : '📄 FACTURE'}
                        </span>
                        <span style={{ fontWeight: '700', color: '#0F172A' }}>{fac.numero}</span>
                      </div>
                      {fac.hash_sha256 && (
                        <div style={{ fontSize: '10px', color: '#94A3B8', fontFamily: 'monospace', marginTop: '4px' }} title={`Hash SHA-256 : ${fac.hash_sha256}`}>
                          🔒 {fac.hash_sha256.substring(0, 16)}...
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '14px 20px', color: '#334155' }}>
                      <div style={{ fontWeight: '600' }}>{fac.client_nom}</div>
                      {fac.client_email && <div style={{ fontSize: '11.5px', color: '#64748B' }}>✉️ {fac.client_email}</div>}
                    </td>
                    <td style={{ padding: '14px 20px', fontWeight: '700', color: isDevis ? '#7E22CE' : '#1D9E75' }}>
                      {formatEuro(fac.total_ttc)}
                    </td>
                    <td style={{ padding: '14px 20px' }}>
                      {isTransmitted ? (
                        <div style={{ background: '#F0F9FF', color: '#0369A1', border: '1px solid #BAE6FD', fontSize: '11px', fontWeight: '700', padding: '4px 10px', borderRadius: '12px', display: 'inline-block' }}>
                          🏛️ PPF Déposé ({fac.numero_depot_ppf?.substring(0, 15)})
                        </div>
                      ) : (
                        <span style={{ color: '#64748B', fontSize: '11.5px' }}>{isDevis ? 'N/A (Devis)' : 'Non transmis'}</span>
                      )}
                    </td>
                    <td style={{ padding: '14px 20px' }}>
                      {isDevis ? (
                        fac.statut_vie === 'annule' ? (
                          <span style={{ background: '#F1F5F9', color: '#64748B', border: '1px solid #CBD5E1', fontSize: '11px', fontWeight: '700', padding: '4px 10px', borderRadius: '12px' }}>
                            ❌ Devis Annulé
                          </span>
                        ) : isConverted ? (
                          <span style={{ background: '#DCFCE7', color: '#15803D', fontSize: '11px', fontWeight: '700', padding: '4px 10px', borderRadius: '12px' }}>
                            ✅ Converti en Facture
                          </span>
                        ) : (
                          <span style={{ background: '#FEF3C7', color: '#D97706', fontSize: '11px', fontWeight: '700', padding: '4px 10px', borderRadius: '12px' }}>
                            ⏳ Devis en attente
                          </span>
                        )
                      ) : isPaid ? (
                        <span style={{ background: '#E1F5EE', color: '#0F6E56', fontSize: '11px', fontWeight: '700', padding: '4px 10px', borderRadius: '12px' }}>
                          💰 Encaissée
                        </span>
                      ) : (
                        <span style={{ background: '#FEF3C7', color: '#D97706', fontSize: '11px', fontWeight: '700', padding: '4px 10px', borderRadius: '12px' }}>
                          📥 Déposée
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '14px 20px', textAlign: 'right', display: 'flex', gap: '6px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                      {isDevis && !isConverted && fac.statut_vie !== 'annule' && (
                        <>
                          <button
                            onClick={() => handleConvertirDevis(fac.id, fac.numero)}
                            title="Convertir ce devis en facture officielle immédiatement"
                            style={{
                              padding: '6px 12px', background: 'linear-gradient(135deg, #7E22CE, #9333EA)', color: 'white',
                              border: 'none', borderRadius: '8px', fontSize: '11.5px',
                              fontWeight: '700', cursor: 'pointer'
                            }}
                          >
                            ⚡ Convertir en Facture
                          </button>
                          <button
                            onClick={() => handleUpdateStatutVie(fac.id, 'annule')}
                            title="Annuler ce devis"
                            style={{
                              padding: '6px 10px', background: '#FEF2F2', color: '#DC2626',
                              border: '1px solid #FCA5A5', borderRadius: '8px', fontSize: '11.5px',
                              fontWeight: '700', cursor: 'pointer'
                            }}
                          >
                            ❌ Annuler
                          </button>
                        </>
                      )}

                      {!isDevis && !isTransmitted && (
                        <button
                          onClick={() => handleTeletransmettrePPF(fac.id)}
                          title="Télétransmettre directement la facture par API au Portail Public (PPF / Chorus Pro)"
                          style={{
                            padding: '6px 10px', background: '#0284C7', color: 'white',
                            border: 'none', borderRadius: '8px', fontSize: '11.5px',
                            fontWeight: '700', cursor: 'pointer'
                          }}
                        >
                          🚀 Transmettre à l'État
                        </button>
                      )}

                      {!isDevis && !isPaid && (
                        <button
                          onClick={() => handleUpdateStatutVie(fac.id, 'encaissee')}
                          title="Marquer la facture comme Encaissée / Payée"
                          style={{
                            padding: '6px 10px', background: '#E1F5EE', color: '#0F6E56',
                            border: '1px solid #A7F3D0', borderRadius: '8px', fontSize: '11.5px',
                            fontWeight: '700', cursor: 'pointer'
                          }}
                        >
                          💰 Payée
                        </button>
                      )}

                      <button
                        onClick={() => handleDownloadFacturX(fac.id, fac.numero)}
                        style={{
                          padding: '6px 12px', background: '#1D9E75', color: 'white',
                          border: 'none', borderRadius: '8px', fontSize: '12px',
                          fontWeight: '600', cursor: 'pointer'
                        }}
                      >
                        📥 Télécharger
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      </>
      );
      })()}

      {/* Modal de création de facture avec Trame et Formulaire Avancé Complet */}
      {showCreateModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(15, 23, 42, 0.5)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: isMobile ? '8px' : '20px',
          boxSizing: 'border-box'
        }}>
          <div style={{ background: 'white', borderRadius: isMobile ? '12px' : '20px', padding: isMobile ? '16px' : '28px', maxWidth: '780px', width: '100%', border: '1px solid #E2E8F0', maxHeight: '92vh', overflowY: 'auto', boxSizing: 'border-box' }}>
            
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
              <div>
                <h2 style={{ fontSize: '20px', fontWeight: '800', color: '#0F172A', margin: 0 }}>
                  {form.type_document === 'devis' ? '📝 Créer un Devis Commercial' : '📄 Créer une Facture Factur-X'}
                </h2>
                <p style={{ fontSize: '13px', color: '#64748B', margin: '2px 0 0' }}>
                  {form.type_document === 'devis' ? 'Le devis pourra être converti en Facture officielle en 1 clic dès validation.' : 'Facture normée conforme à la réforme 2026/2027.'}
                </p>
              </div>
              <button onClick={() => setShowCreateModal(false)} style={{ background: 'none', border: 'none', fontSize: '22px', color: '#94A3B8', cursor: 'pointer' }}>×</button>
            </div>

            {/* Selector Document Type (Devis vs Facture) */}
            <div style={{ display: 'flex', background: '#F1F5F9', padding: '4px', borderRadius: '10px', marginBottom: '16px' }}>
              <button
                type="button"
                onClick={() => setForm({ ...form, type_document: 'devis' })}
                style={{
                  flex: 1, padding: '8px', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '700',
                  background: form.type_document === 'devis' ? '#7E22CE' : 'transparent',
                  color: form.type_document === 'devis' ? 'white' : '#64748B',
                  cursor: 'pointer'
                }}
              >
                📝 DEVIS
              </button>
              <button
                type="button"
                onClick={() => setForm({ ...form, type_document: 'facture' })}
                style={{
                  flex: 1, padding: '8px', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '700',
                  background: form.type_document === 'facture' ? '#1D9E75' : 'transparent',
                  color: form.type_document === 'facture' ? 'white' : '#64748B',
                  cursor: 'pointer'
                }}
              >
                📄 FACTURE (Factur-X)
              </button>
            </div>

            {/* Sélecteur de Trame Métier */}
            <div style={{ background: '#F0F9FF', border: '1px solid #BAE6FD', borderRadius: '12px', padding: '12px 16px', marginBottom: '20px' }}>
              <label style={{ fontSize: '12px', fontWeight: '700', color: '#0369A1', display: 'block', marginBottom: '6px', textTransform: 'uppercase' }}>
                📋 Trame & Secteur d'activité :
              </label>
              <select
                value={form.secteur_activite}
                onChange={(e) => handleSecteurChange(e.target.value)}
                style={{ width: '100%', padding: '10px', border: '1px solid #7DD3FC', borderRadius: '8px', fontSize: '13.5px', fontWeight: '600', color: '#0F172A', background: 'white' }}
              >
                {Object.entries(PRESETS_SECTEURS).map(([key, val]) => (
                  <option key={key} value={key}>{val.label}</option>
                ))}
              </select>
            </div>

            <form onSubmit={handleCreateInvoice} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              
              {/* Sélecteur de Client Récurrent */}
              {clientsRecurrents.length > 0 && (
                <div style={{ background: '#F8FAFC', border: '1px solid #CBD5E1', borderRadius: '10px', padding: '10px 14px' }}>
                  <label style={{ fontSize: '12px', fontWeight: '700', color: '#0F172A', display: 'block', marginBottom: '4px' }}>
                    👥 Pré-remplir depuis un client récurrent :
                  </label>
                  <select
                    onChange={(e) => handleSelectClientRecurrent(e.target.value)}
                    defaultValue=""
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #CBD5E1', borderRadius: '6px', fontSize: '13px', color: '#0F172A', background: 'white' }}
                  >
                    <option value="" disabled>-- Sélectionnez un client déjà facturé --</option>
                    {clientsRecurrents.map((c, idx) => (
                      <option key={idx} value={c.client_nom}>
                        {c.client_nom} {c.client_siren ? `(SIREN: ${c.client_siren})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Informations Client */}
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ fontSize: '12px', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '4px' }}>Nom du client *</label>
                  <input
                    type="text"
                    placeholder="Ex: ACME Corporation"
                    value={form.client_nom}
                    onChange={(e) => setForm({ ...form, client_nom: e.target.value })}
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #CBD5E1', borderRadius: '8px', fontSize: '13.5px', boxSizing: 'border-box' }}
                    required
                  />
                </div>

                <div>
                  <label style={{ fontSize: '12px', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '4px' }}>Email du destinataire</label>
                  <input
                    type="email"
                    placeholder="facturation@client.com"
                    value={form.client_email}
                    onChange={(e) => setForm({ ...form, client_email: e.target.value })}
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #CBD5E1', borderRadius: '8px', fontSize: '13.5px', boxSizing: 'border-box' }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ fontSize: '12px', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '4px' }}>SIREN client</label>
                  <input
                    type="text"
                    placeholder="Ex: 987654321"
                    value={form.client_siren}
                    onChange={(e) => setForm({ ...form, client_siren: e.target.value })}
                    style={{ width: '100%', padding: '9px 12px', border: '1px solid #CBD5E1', borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box' }}
                  />
                </div>

                <div>
                  <label style={{ fontSize: '12px', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '4px' }}>Adresse complète client</label>
                  <input
                    type="text"
                    placeholder="Ex: 12 Rue de la Paix, Paris"
                    value={form.client_adresse}
                    onChange={(e) => setForm({ ...form, client_adresse: e.target.value })}
                    style={{ width: '100%', padding: '9px 12px', border: '1px solid #CBD5E1', borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box' }}
                  />
                </div>

                <div>
                  <label style={{ fontSize: '12px', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '4px' }}>Date d'émission</label>
                  <input
                    type="date"
                    value={form.date_facture}
                    onChange={(e) => setForm({ ...form, date_facture: e.target.value })}
                    style={{ width: '100%', padding: '9px 12px', border: '1px solid #CBD5E1', borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box' }}
                  />
                </div>
              </div>

              {/* Lignes d'articles */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <label style={{ fontSize: '13px', fontWeight: '700', color: '#0F172A' }}>Lignes de prestation / produits</label>
                  <button
                    type="button"
                    onClick={handleAddLigne}
                    style={{ background: 'none', border: 'none', color: '#1D9E75', fontSize: '12.5px', fontWeight: '700', cursor: 'pointer' }}
                  >
                    + Ajouter une ligne
                  </button>
                </div>

                <div style={{ width: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch', paddingBottom: '4px' }}>
                  <div style={{ minWidth: isMobile ? '480px' : 'auto' }}>
                    {form.lignes.map((lg, idx) => (
                      <div key={idx} style={{ display: 'grid', gridTemplateColumns: '2fr 70px 90px 90px 30px', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
                        <input
                          type="text"
                          placeholder="Désignation de la prestation..."
                          value={lg.description}
                          onChange={(e) => handleLigneChange(idx, 'description', e.target.value)}
                          style={{ padding: '8px 10px', border: '1px solid #CBD5E1', borderRadius: '6px', fontSize: '13px', width: '100%', boxSizing: 'border-box' }}
                          required
                        />
                        <input
                          type="number"
                          placeholder="Qté"
                          value={lg.quantite}
                          onChange={(e) => handleLigneChange(idx, 'quantite', parseFloat(e.target.value))}
                          style={{ padding: '8px 10px', border: '1px solid #CBD5E1', borderRadius: '6px', fontSize: '13px', width: '100%', boxSizing: 'border-box' }}
                          required
                        />
                        <input
                          type="number"
                          placeholder="P.U. HT"
                          value={lg.prix_unitaire_ht}
                          onChange={(e) => handleLigneChange(idx, 'prix_unitaire_ht', parseFloat(e.target.value))}
                          style={{ padding: '8px 10px', border: '1px solid #CBD5E1', borderRadius: '6px', fontSize: '13px', width: '100%', boxSizing: 'border-box' }}
                          required
                        />
                        <select
                          value={lg.taux_tva}
                          onChange={(e) => handleLigneChange(idx, 'taux_tva', parseFloat(e.target.value))}
                          style={{ padding: '8px 4px', border: '1px solid #CBD5E1', borderRadius: '6px', fontSize: '12px', width: '100%', boxSizing: 'border-box' }}
                        >
                          <option value={20.0}>20%</option>
                          <option value={10.0}>10%</option>
                          <option value={5.5}>5.5%</option>
                          <option value={2.1}>2.1%</option>
                          <option value={0.0}>0% (Exo)</option>
                        </select>

                        {form.lignes.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveLigne(idx)}
                            style={{ background: '#FEE2E2', color: '#DC2626', border: 'none', borderRadius: '6px', padding: '6px', cursor: 'pointer', fontWeight: 'bold' }}
                          >
                            ×
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Coordonnées Bancaires & Mentions Légales */}
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ fontSize: '12px', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '4px' }}>IBAN (Pour règlement par virement)</label>
                  <input
                    type="text"
                    placeholder="FR76 1234 5678 9012 3456 7890 123"
                    value={form.iban}
                    onChange={(e) => setForm({ ...form, iban: e.target.value })}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #CBD5E1', borderRadius: '8px', fontSize: '12.5px', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '12px', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '4px' }}>Mentions légales (Auto-remplies par secteur)</label>
                  <input
                    type="text"
                    value={form.mentions_legales}
                    onChange={(e) => setForm({ ...form, mentions_legales: e.target.value })}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #CBD5E1', borderRadius: '8px', fontSize: '12.5px', boxSizing: 'border-box' }}
                  />
                </div>
              </div>

              {/* Totaux Calculés */}
              <div style={{ background: '#F8FAFC', borderRadius: '12px', padding: '14px 18px', border: '1px solid #E2E8F0', marginTop: '4px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#64748B' }}>
                  <span>Total HT :</span>
                  <strong>{formatEuro(totalHT)}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#64748B', marginTop: '4px' }}>
                  <span>Total TVA :</span>
                  <strong>{formatEuro(totalTVA)}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '15px', fontWeight: '800', color: '#0F172A', borderTop: '1px solid #E2E8F0', paddingTop: '8px', marginTop: '8px' }}>
                  <span>Net à payer (TTC) :</span>
                  <span style={{ color: '#1D9E75' }}>{formatEuro(totalTTC)}</span>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '6px' }}>
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  style={{ padding: '10px 16px', background: '#F1F5F9', color: '#475569', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}
                >
                  Annuler
                </button>

                <button
                  type="submit"
                  disabled={creating}
                  style={{ padding: '10px 20px', background: '#1D9E75', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}
                >
                  {creating ? 'Scellement SHA-256...' : 'Créer & Sceller Factur-X'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default FacturationEinvoicing;
